import { ethers } from 'ethers';
import { PERPETUAL_RATE_DENOMINATOR, SECONDS_PER_YEAR } from '../config/config';

/**
 * Начисление по бессрочной позиции — то же, что считает контракт.
 *
 * Бэкенд раз в минуту снимает `perpetualPending` и отдаёт его вместе с
 * временем блока (`pendingAt`) и сырой ставкой. Панель доращивает значение
 * локально, раз в секунду: читать контракт из браузера значило бы «позиций ×
 * вкладок» вызовов RPC в минуту и вынос ключа провайдера в бандл.
 *
 * Счёт идёт в `BigInt` по минимальным единицам токена — ровно как в Solidity.
 * Через `Number` тело в 10 000 EMIVN (1e22 wei) уже теряет разряды, и счётчик
 * начинал бы врать на последних знаках.
 */
export const projectPending = (
  { pendingRaw, principalRaw, aprRaw, pendingAt },
  now = Date.now(),
) => {
  const pending = BigInt(pendingRaw ?? '0');

  // Снимка ещё не было: позиция только появилась, фоновый цикл до неё не
  // дошёл. Показываем то, что есть, и не выдумываем начисление.
  if (!pendingAt || !aprRaw) return pending;

  const elapsed = Math.floor((now - new Date(pendingAt).getTime()) / 1000);

  // Часы браузера и время блока — разные источники. Снимок «из будущего»
  // означает расхождение часов, а не отрицательное начисление.
  if (elapsed <= 0) return pending;

  const accrued =
    (BigInt(principalRaw) * BigInt(aprRaw) * BigInt(elapsed)) /
    (BigInt(PERPETUAL_RATE_DENOMINATOR) * BigInt(SECONDS_PER_YEAR));

  return pending + accrued;
};

/**
 * Минимальные единицы → строка для таблицы.
 *
 * Знаков после запятой намеренно много: счётчик тикает раз в секунду, и при
 * двух знаках у небольшой позиции он менялся бы раз в несколько минут —
 * выглядело бы как зависшая страница.
 */
export const formatPending = (raw, decimals = 6) => {
  const value = Number(ethers.formatEther(raw));

  return value.toLocaleString('ru-RU', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/** Минимальные единицы → строка без дробной мелочи: тело, выплаты. */
export const formatTokens = (value, decimals = 2) =>
  Number(value).toLocaleString('ru-RU', { maximumFractionDigits: decimals });
